/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Award, Sparkles, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const SCORE_COLOR = (n) =>
    n >= 70 ? "#7fb069" : n >= 50 ? "#e6a23c" : "#e89090";

const STATUS = {
    in_progress: { l: "En cours", c: "#e6a23c" },
    completed: { l: "Validée", c: "#7fb069" },
    failed: { l: "Insuffisante", c: "#e89090" },
    abandoned: { l: "Abandonnée", c: "#f4ecd8" },
};

export default function CockpitCommitteePanel({ dossierId }) {
    const [state, setState] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [s, ss] = await Promise.all([
                    cockpit.get(`/dossiers/${dossierId}/committee/state`),
                    cockpit.get(`/dossiers/${dossierId}/committee/sessions`),
                ]);
                setState(s.data);
                setSessions(ss.data.items || []);
            } catch {
                setState({ unlocked: false });
            }
        };
        load();
    }, [dossierId]);

    if (!state) return null;

    return (
        <section
            data-testid="cockpit-committee-panel"
            className="mt-16 border-t border-white/[0.06] pt-12"
        >
            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-2">
                        <Sparkles
                            strokeWidth={1}
                            size={13}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        XV Investment Committee Simulator™
                    </div>
                    <h2
                        className="font-serif text-[26px] sm:text-[30px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Préparation Investisseurs — suivi
                    </h2>
                    <p className="text-[12.5px] text-[#f4ecd8]/50 italic mt-2 max-w-2xl">
                        {state.unlocked
                            ? "Module activé pour ce client (étape Discussions confidentielles atteinte)."
                            : "Module verrouillé pour le client tant que l'étape 5 « Discussions confidentielles » n'est pas atteinte."}
                    </p>
                </div>
                {state.certified && (
                    <div className="inline-flex items-center gap-3 border border-[#d4af37] bg-[#d4af37]/[0.06] px-5 py-3">
                        <Award strokeWidth={1.2} size={20} className="text-[#d4af37]" />
                        <div>
                            <div className="font-serif text-[15px] text-[#f4ecd8]">
                                XV INVESTMENT READY
                            </div>
                            <div className="text-[9px] tracking-[0.26em] uppercase text-[#d4af37]/80">
                                Moyenne {state.average_score} %
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8">
                {(state.series || []).map((s) => (
                    <div
                        key={s.series}
                        data-testid={`cockpit-committee-series-${s.series}`}
                        className={`border p-4 ${
                            s.validated
                                ? "border-[#d4af37]/50 bg-[#d4af37]/[0.04]"
                                : "border-white/[0.08] bg-white/[0.02]"
                        }`}
                    >
                        <div className="overline text-[#d4af37]/70 mb-1">
                            Série {s.series}
                        </div>
                        <div className="font-serif text-[14px] text-[#f4ecd8] mb-2 leading-tight">
                            {s.title}
                        </div>
                        <div
                            className="font-serif text-[22px]"
                            style={{ color: SCORE_COLOR(s.best_score) }}
                        >
                            {s.best_score} %
                        </div>
                        <div className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/40 mt-1">
                            {s.attempts} tentative{s.attempts > 1 ? "s" : ""}
                        </div>
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="cockpit-committee-history-toggle"
                className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 hover:text-[#d4af37] inline-flex items-center gap-2 mb-4"
            >
                {open ? (
                    <ChevronUp strokeWidth={1.3} size={13} />
                ) : (
                    <ChevronDown strokeWidth={1.3} size={13} />
                )}
                {sessions.length} tentative{sessions.length > 1 ? "s" : ""} enregistrée{sessions.length > 1 ? "s" : ""}
            </button>

            {open && (
                <ul className="space-y-2">
                    {sessions.map((s) => (
                        <li
                            key={s.id}
                            data-testid={`cockpit-committee-session-${s.id}`}
                            className="flex items-center gap-3 border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                            onClick={async () => {
                                const { data } = await cockpit.get(
                                    `/dossiers/${dossierId}/committee/sessions/${s.id}`,
                                );
                                setDetail(data);
                            }}
                        >
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/70 w-16">
                                S{s.series}
                            </span>
                            <span className="text-[11px] text-[#f4ecd8]/55 w-24">
                                Tentative {s.attempt}
                            </span>
                            <span className="text-[11px] text-[#f4ecd8]/40 flex-1">
                                {s.started_at
                                    ? new Date(s.started_at).toLocaleString("fr-FR")
                                    : ""}
                            </span>
                            <span className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/45 w-24 inline-flex items-center gap-1.5">
                                {s.mode === "requin" && (
                                    <Zap strokeWidth={1.4} size={10} className="text-[#e89090]" />
                                )}
                                {s.mode === "requin" ? "Requin" : "Standard"}
                            </span>
                            <span
                                className="font-serif text-[15px] w-12 text-right"
                                style={{ color: SCORE_COLOR(s.score || 0) }}
                            >
                                {s.score || 0} %
                            </span>
                            <span
                                className="text-[10px] tracking-[0.26em] uppercase w-24 text-right"
                                style={{ color: STATUS[s.status]?.c || "#f4ecd8" }}
                            >
                                {STATUS[s.status]?.l || s.status}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {detail && <SessionDetailModal session={detail} onClose={() => setDetail(null)} />}
        </section>
    );
}

function SessionDetailModal({ session, onClose }) {
    const r = session.report || {};
    return (
        <div
            data-testid="cockpit-committee-session-detail"
            className="fixed inset-0 z-50 bg-[#040810]/97 backdrop-blur-sm overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="max-w-4xl mx-auto px-6 py-12"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-baseline justify-between mb-8 gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-2">
                            Série {session.series} · Tentative {session.attempt} · Mode{" "}
                            {session.mode === "requin" ? "Requin" : "Standard"}
                        </div>
                        <h2
                            className="font-serif text-[26px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            Session — score {session.score} %
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/55 hover:text-[#f4ecd8] border border-white/15 px-4 py-2"
                    >
                        Fermer
                    </button>
                </div>

                {session.answers && session.answers.length > 0 && (
                    <div className="mb-10">
                        <div className="overline text-[#d4af37]/70 mb-4">
                            20 questions & réponses
                        </div>
                        <ul className="space-y-5">
                            {session.answers.map((a) => (
                                <li
                                    key={a.question_index}
                                    className="border-l border-[#d4af37]/30 pl-5"
                                >
                                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/60 mb-1">
                                        Q{a.question_index} · Note {a.scores.average}/100
                                    </div>
                                    <div className="font-serif text-[14.5px] text-[#f4ecd8] mb-2">
                                        {a.question_text}
                                    </div>
                                    <p className="text-[12.5px] text-[#f4ecd8]/65 italic font-light leading-relaxed">
                                        {a.answer}
                                    </p>
                                    {a.scores.comment && (
                                        <p className="text-[11.5px] text-[#d4af37]/70 mt-2 leading-relaxed">
                                            {a.scores.comment}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {Object.keys(r).length > 0 && (
                    <div className="space-y-6">
                        <div className="overline text-[#d4af37]/70">Rapport IA</div>
                        {Object.entries(r).map(([k, items]) => (
                            (items || []).length > 0 && (
                                <div key={k} className="border-l border-[#d4af37]/30 pl-5">
                                    <div className="overline text-[#f4ecd8]/60 mb-2">
                                        {k.replaceAll("_", " ")}
                                    </div>
                                    <ul className="space-y-1.5">
                                        {items.map((it, i) => (
                                            <li
                                                key={i}
                                                className="text-[13px] text-[#f4ecd8]/80 font-light leading-relaxed"
                                            >
                                                — {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
